import {
  ButtonBase, Checkbox, List, ListItem, Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ImportPage } from 'services/imports/fileToPages';
import { MAX_SELECTED_PAGES } from 'services/imports/limits';
import { PageRowMeta } from 'views/imports/components/page-row-meta';
import { PageSelectionBar } from 'views/imports/components/page-selection-bar';
import { PageThumb } from 'views/imports/components/page-thumb';
import { SourcePreviewDialog } from 'views/imports/components/source-preview-dialog';
import type { OcrPageState } from 'views/imports/types';
import { countPendingSelected } from 'views/imports/use-page-selection';

const NO_SELECTION: ReadonlySet<string> = new Set();

const useStyles = makeStyles((theme: Theme) => createStyles({
  heading: {
    marginBottom: theme.spacing(1),
  },
  scroller: {
    maxHeight: theme.spacing(45),
    overflowY: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
  row: {
    alignItems: 'stretch',
    minHeight: theme.spacing(5.5),
    padding: theme.spacing(0, 0, 0, 1),
    borderLeft: '3px solid transparent',
  },
  selected: {
    borderLeftColor: theme.palette.primary.main,
  },
  checkbox: {
    flexShrink: 0,
    alignSelf: 'center',
    padding: theme.spacing(1.25),
    ...(theme.palette.mode === 'dark' && {
      '&&.Mui-checked': { color: theme.palette.text.primary },
    }),
  },
  checkboxSlot: {
    flexShrink: 0,
    width: theme.spacing(5.5),
  },
  main: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 2, 1, 0.75),
    textAlign: 'left',
  },
  mainButton: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  focusVisible: {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
  empty: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

type PageListProps = {
  pages: readonly ImportPage[];
  ocrByPage: ReadonlyMap<string, OcrPageState>;
  extractedPageIds: ReadonlySet<string>;
  selectedPageId?: string | null;
  onSelectPage?: (pageId: string) => void;
  selectedPageIds?: ReadonlySet<string>;
  maxSelected?: number;
  onToggleSelected?: (pageId: string) => void;
  onClearSelection?: () => void;
  selectionDisabled?: boolean;
};

export const PageList = ({
  pages,
  ocrByPage,
  extractedPageIds,
  selectedPageId = null,
  onSelectPage,
  selectedPageIds = NO_SELECTION,
  maxSelected = MAX_SELECTED_PAGES,
  onToggleSelected,
  onClearSelection,
  selectionDisabled = false,
}: PageListProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [dialogPageId, setDialogPageId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const dialogPage = pages.find((page) => page.id === dialogPageId) ?? null;
  const selectedCount = countPendingSelected(pages, selectedPageIds);
  const limitReached = selectedCount >= maxSelected;

  // Failed pages keep an empty slot so every row's thumbnail stays aligned.
  const renderCheckbox = (page: ImportPage, toggle: (pageId: string) => void) => {
    if (page.failed) return <span className={classes.checkboxSlot} />;
    const checked = selectedPageIds.has(page.id);
    const blocked = limitReached;
    const label: string = t('imports:pages.select', { file: page.fileName, page: page.pageNumber });

    return (
      <Checkbox
        color="primary"
        checked={checked}
        disabled={selectionDisabled || (!checked && blocked)}
        onChange={() => toggle(page.id)}
        inputProps={{ 'aria-label': label }}
        className={classes.checkbox}
      />
    );
  };

  // The row is a plain list item; the checkbox, thumbnail and main area are sibling controls, never nested.
  const renderRow = (page: ImportPage) => {
    const selectPage = page.failed ? undefined : onSelectPage;
    const isPreviewed = selectPage !== undefined && page.id === selectedPageId;
    const previewLabel: string = t('imports:pages.preview', { file: page.fileName, page: page.pageNumber });
    const meta = <PageRowMeta page={page} ocr={ocrByPage.get(page.id)} extracted={extractedPageIds.has(page.id)} />;
    const openPreview = selectPage ? () => {
      selectPage(page.id);
      setDialogPageId(page.id);
      setDialogOpen(true);
    } : undefined;

    return (
      <ListItem
        key={page.id}
        divider
        disableGutters
        className={clsx(classes.row, isPreviewed && classes.selected)}
      >
        {onToggleSelected && renderCheckbox(page, onToggleSelected)}
        <PageThumb page={page} onOpen={openPreview} />
        {selectPage ? (
          <ButtonBase
            component="div"
            aria-label={previewLabel}
            aria-current={isPreviewed ? 'true' : undefined}
            focusVisibleClassName={classes.focusVisible}
            className={`${classes.main} ${classes.mainButton}`}
            onClick={() => selectPage(page.id)}
          >
            {meta}
          </ButtonBase>
        ) : (
          <div className={classes.main}>{meta}</div>
        )}
      </ListItem>
    );
  };

  return (
    <section aria-label={t('imports:pages.title')}>
      <Typography variant="subtitle2" component="h2" className={classes.heading}>
        {t('imports:pages.title')}
      </Typography>
      {onClearSelection && pages.length > 0 && (
        <PageSelectionBar
          selectedCount={selectedCount}
          maxSelected={maxSelected}
          disabled={selectionDisabled}
          onClear={onClearSelection}
        />
      )}
      <div className={classes.scroller}>
        {pages.length === 0 ? (
          <Typography variant="body2" className={classes.empty}>
            {t('imports:pages.empty')}
          </Typography>
        ) : (
          <List dense disablePadding>
            {pages.map(renderRow)}
          </List>
        )}
      </div>
      {dialogPage && (
        <SourcePreviewDialog page={dialogPage} open={dialogOpen} onClose={() => setDialogOpen(false)} />
      )}
    </section>
  );
};
